/**
 * Frontend API Client
 * Provides secure, type-safe API calls with automatic authentication handling
 */

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  status: number;
}

export interface ApiOptions extends RequestInit {
  skipAuthRedirect?: boolean;
}

/**
 * Make an authenticated API call
 * Automatically handles:
 * - Including credentials (cookies)
 * - 401/403 errors with user feedback
 * - Network errors
 * - JSON parsing
 */
export async function apiCall<T = any>(
  url: string,
  options?: ApiOptions
): Promise<ApiResponse<T>> {
  const { skipAuthRedirect, ...fetchOptions } = options || {};

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include', // Always include cookies for authentication
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions?.headers,
      },
    });

    // Handle authentication errors
    if (response.status === 401) {
      if (!skipAuthRedirect && typeof window !== 'undefined') {
        // Show toast message if available
        const toast = (window as any).toast;
        if (toast) {
          toast.error('Please sign in to continue');
        }

        // Redirect to login with return URL
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/api/auth/login?returnTo=${returnTo}`;
      }

      return {
        error: 'Authentication required',
        status: 401,
      };
    }

    // Handle authorization errors
    if (response.status === 403) {
      if (typeof window !== 'undefined') {
        const toast = (window as any).toast;
        if (toast) {
          toast.error('Access denied - you do not have permission to perform this action');
        }
      }

      return {
        error: 'Access denied',
        status: 403,
      };
    }

    // Handle not found errors
    if (response.status === 404) {
      const errorData = await response.json().catch(() => ({ error: 'Resource not found' }));
      return {
        error: errorData.error || 'Resource not found',
        status: 404,
      };
    }

    // Handle other error responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));

      if (typeof window !== 'undefined') {
        const toast = (window as any).toast;
        if (toast && response.status >= 500) {
          toast.error('Server error - please try again later');
        }
      }

      return {
        error: errorData.error || `HTTP ${response.status}`,
        status: response.status,
      };
    }

    // Success - parse JSON response
    const data = await response.json();

    return {
      data,
      status: response.status,
    };
  } catch (error) {
    console.error('API call failed:', error);

    // Network error or other exception
    if (typeof window !== 'undefined') {
      const toast = (window as any).toast;
      if (toast) {
        toast.error('Network error - please check your connection');
      }
    }

    return {
      error: error instanceof Error ? error.message : 'Network error',
      status: 0, // 0 indicates network failure
    };
  }
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  /**
   * GET request
   */
  get: <T = any>(url: string, options?: ApiOptions) =>
    apiCall<T>(url, { ...options, method: 'GET' }),

  /**
   * POST request
   */
  post: <T = any>(url: string, data?: any, options?: ApiOptions) =>
    apiCall<T>(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * PUT request
   */
  put: <T = any>(url: string, data?: any, options?: ApiOptions) =>
    apiCall<T>(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * PATCH request
   */
  patch: <T = any>(url: string, data?: any, options?: ApiOptions) =>
    apiCall<T>(url, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * DELETE request
   */
  delete: <T = any>(url: string, options?: ApiOptions) =>
    apiCall<T>(url, { ...options, method: 'DELETE' }),
};

/**
 * Type-safe API client for SplitCause
 */
export const splitcauseApi = {
  // Graphs
  getGraph: (id: string) =>
    api.get<any>(`/api/secure/splitcause/graphs/${id}`),

  createGraph: (data: { incidentId: string; title: string }) =>
    api.post<any>('/api/secure/splitcause/graphs', data),

  updateGraph: (id: string, data: { title?: string; isCurrent?: boolean }) =>
    api.patch<any>(`/api/secure/splitcause/graphs/${id}`, data),

  deleteGraph: (id: string) =>
    api.delete<any>(`/api/secure/splitcause/graphs/${id}`),

  // Nodes
  createNode: (data: any) =>
    api.post<any>('/api/secure/splitcause/nodes', data),

  updateNode: (id: string, data: any) =>
    api.patch<any>(`/api/secure/splitcause/nodes/${id}`, data),

  deleteNode: (id: string) =>
    api.delete<any>(`/api/secure/splitcause/nodes/${id}`),

  // Edges
  createEdge: (data: any) =>
    api.post<any>('/api/secure/splitcause/edges', data),

  updateEdge: (id: string, data: any) =>
    api.patch<any>(`/api/secure/splitcause/edges/${id}`, data),

  deleteEdge: (id: string) =>
    api.delete<any>(`/api/secure/splitcause/edges/${id}`),

  // Evidence
  createEvidence: (data: any) =>
    api.post<any>('/api/secure/splitcause/evidence', data),

  updateEvidence: (id: string, data: any) =>
    api.patch<any>(`/api/secure/splitcause/evidence/${id}`, data),

  deleteEvidence: (id: string) =>
    api.delete<any>(`/api/secure/splitcause/evidence/${id}`),

  // Snapshots
  createSnapshot: (data: any) =>
    api.post<any>('/api/secure/splitcause/snapshots', data),

  getSnapshot: (id: string) =>
    api.get<any>(`/api/secure/splitcause/snapshots/${id}`),

  // Share
  createShareLink: (data: any) =>
    api.post<any>('/api/secure/splitcause/share', data),

  // Export
  exportGraph: (graphId: string, format: string) =>
    api.post<any>('/api/secure/splitcause/export', { graphId, format }),

  // Inference
  runInference: (data: any) =>
    api.post<any>('/api/secure/splitcause/infer', data),
};

/**
 * Type-safe API client for ArchiMate
 */
export const archimateApi = {
  // Workspaces
  listWorkspaces: () =>
    api.get<any[]>('/api/secure/archimate/workspaces'),

  getWorkspace: (id: string) =>
    api.get<any>(`/api/secure/archimate/workspaces/${id}`),

  createWorkspace: (data: any) =>
    api.post<any>('/api/secure/archimate/workspaces', data),

  updateWorkspace: (id: string, data: any) =>
    api.put<any>(`/api/secure/archimate/workspaces/${id}`, data),

  deleteWorkspace: (id: string) =>
    api.delete<any>(`/api/secure/archimate/workspaces/${id}`),

  // Diagrams
  listDiagrams: (workspaceId: string) =>
    api.get<any[]>(`/api/secure/archimate/workspaces/${workspaceId}/diagrams`),

  getDiagram: (workspaceId: string, diagramId: string) =>
    api.get<any>(`/api/secure/archimate/workspaces/${workspaceId}/diagrams/${diagramId}`),

  createDiagram: (workspaceId: string, data: any) =>
    api.post<any>(`/api/secure/archimate/workspaces/${workspaceId}/diagrams`, data),

  updateDiagram: (workspaceId: string, diagramId: string, data: any) =>
    api.put<any>(`/api/secure/archimate/workspaces/${workspaceId}/diagrams/${diagramId}`, data),

  deleteDiagram: (workspaceId: string, diagramId: string) =>
    api.delete<any>(`/api/secure/archimate/workspaces/${workspaceId}/diagrams/${diagramId}`),
};

/**
 * Helper to build query strings from params
 */
function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return entries.length > 0 ? `?${entries.join('&')}` : '';
}

/**
 * Type-safe API client for Command Center
 */
const CC = '/api/secure/command-center';
export const commandCenterApi = {
  entities: {
    list: (workspaceId: string, params?: Record<string, string>) =>
      api.get<any>(`${CC}/entities${buildQuery({ workspace_id: workspaceId, ...params })}`),
    create: (data: any) =>
      api.post<any>(`${CC}/entities`, data),
    get: (id: string) =>
      api.get<any>(`${CC}/entities/${id}`),
    update: (id: string, data: any) =>
      api.put<any>(`${CC}/entities/${id}`, data),
    delete: (id: string) =>
      api.delete<any>(`${CC}/entities/${id}`),
  },
  assessments: {
    list: (workspaceId: string) =>
      api.get<any>(`${CC}/assessments${buildQuery({ workspace_id: workspaceId })}`),
    create: (data: any) =>
      api.post<any>(`${CC}/assessments`, data),
  },
  incidents: {
    list: (workspaceId: string) =>
      api.get<any>(`${CC}/incidents${buildQuery({ workspace_id: workspaceId })}`),
    create: (data: any) =>
      api.post<any>(`${CC}/incidents`, data),
  },
  decisions: {
    list: (workspaceId: string, params?: Record<string, string>) =>
      api.get<any>(`${CC}/decisions${buildQuery({ workspace_id: workspaceId, ...params })}`),
    create: (data: any) =>
      api.post<any>(`${CC}/decisions`, data),
  },
  nlQuery: (workspaceId: string, question: string) =>
    api.post<any>(`${CC}/nl-query`, { workspace_id: workspaceId, question }),
  architectureReview: (workspaceId: string) =>
    api.post<any>(`${CC}/architecture-review`, { workspace_id: workspaceId }),
  benchmarks: {
    get: (workspaceId: string) =>
      api.get<any>(`${CC}/benchmarks${buildQuery({ workspace_id: workspaceId })}`),
    submit: (data: any) =>
      api.post<any>(`${CC}/benchmarks`, data),
  },
  reports: {
    getData: (workspaceId: string, type: string) =>
      api.get<any>(`${CC}/reports/data${buildQuery({ workspace_id: workspaceId, type })}`),
  },
  compliancePosture: (workspaceId: string) =>
    api.get<any>(`${CC}/compliance/posture${buildQuery({ workspace_id: workspaceId })}`),
  dueDiligence: {
    list: (workspaceId: string) =>
      api.get<any>(`${CC}/due-diligence${buildQuery({ workspace_id: workspaceId })}`),
    create: (data: any) =>
      api.post<any>(`${CC}/due-diligence`, data),
    report: (sessionId: string, workspaceId: string) =>
      api.get<any>(`${CC}/due-diligence/report${buildQuery({ session_id: sessionId, workspace_id: workspaceId })}`),
  },
  onboardingPacks: {
    list: (workspaceId: string) =>
      api.get<any>(`${CC}/onboarding-packs${buildQuery({ workspace_id: workspaceId })}`),
    create: (data: any) =>
      api.post<any>(`${CC}/onboarding-packs`, data),
    assign: (data: any) =>
      api.post<any>(`${CC}/onboarding-packs/assign`, data),
    assignments: (workspaceId: string, packId?: string) =>
      api.get<any>(`${CC}/onboarding-packs/assign${buildQuery({ workspace_id: workspaceId, pack_id: packId })}`),
  },
  techRecommendations: (workspaceId: string) =>
    api.post<any>(`${CC}/tech-recommendations`, { workspace_id: workspaceId }),
};

/**
 * Type-safe API client for API Keys & Webhooks
 */
export const apiKeysApi = {
  list: (workspaceId?: string) =>
    api.get<any>(`/api/secure/api-keys${buildQuery({ workspace_id: workspaceId })}`),
  create: (data: any) =>
    api.post<any>('/api/secure/api-keys', data),
  revoke: (id: string) =>
    api.delete<any>(`/api/secure/api-keys/${id}`),
};

export const webhooksApi = {
  list: (workspaceId: string) =>
    api.get<any>(`/api/secure/webhooks${buildQuery({ workspace_id: workspaceId })}`),
  create: (data: any) =>
    api.post<any>('/api/secure/webhooks', data),
};

/**
 * Check if user is authenticated
 */
export async function checkAuth(): Promise<{ authenticated: boolean; user?: any }> {
  const response = await apiCall<{ authenticated: boolean; user?: any }>('/api/auth/me', {
    skipAuthRedirect: true,
  });

  if (response.error) {
    return { authenticated: false };
  }

  return response.data || { authenticated: false };
}
